import type { 
  TenantContext, 
  TenantValidationResult, 
  TenantRequestContext,
  TenantConfig,
  MultiTenantUser,
  TenantAwareData
} from '@/lib/types/tenant';
import type { User, Organization } from '@/lib/types';
import { serviceLogger } from '@/lib/utils/logger';

const logger = serviceLogger('TenantManager');

/**
 * Tenant Manager
 * Streamlined multi-tenant operations with Firebase Auth integration
 * 
 * Features:
 * - Tenant context extraction and validation
 * - Data isolation and filtering
 * - Super Admin bypass logic
 * - Organization-tenant mapping
 * - Audit logging capabilities
 */
export class TenantManager {
  private static instance: TenantManager;
  private tenantConfig: TenantConfig = {
    isolationLevel: 'strict',
    enforceStrictAccess: true,
    allowCrossTenantAccess: false,
    auditDataAccess: true,
  };

  private constructor() {
    // Only log instance creation once per JavaScript context
    if (typeof window === 'undefined') {
      // Server-side
      logger.debug('TenantManager singleton instance created (server-side)');
    } else {
      // Client-side - only log if this is truly the first instance
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (!(window as any).__tenantManagerInitialized) {
        logger.debug('TenantManager singleton instance created (client-side)');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).__tenantManagerInitialized = true;
      }
    }
  }

  public static getInstance(): TenantManager {
    if (!TenantManager.instance) {
      TenantManager.instance = new TenantManager();
    }
    return TenantManager.instance;
  }

  /**
   * Extract tenant context from authenticated user
   */
  extractTenantContext(user: User): TenantContext | null {
    try {
      if (!user?.organizationId || !user?.tenantId || !user?.status) {
        logger.warn('Invalid user data for tenant context extraction', { 
          userUid: user.uid,
          hasOrganizationId: !!user?.organizationId,
          hasTenantId: !!user?.tenantId,
          hasStatus: !!user?.status
        });
        return null;
      }

      const context: TenantContext = {
        tenantId: user.tenantId,
        organizationId: user.organizationId,
        organizationName: undefined,
        isActive: user.status === 'Active',
        features: {
          multiFactorAuth: true,
          federatedIdentity: false,
          customClaims: true,
          auditLogging: true,
          advancedSecurity: true,
          customBranding: false,
          apiAccess: true,
          webhooks: false
        }
      };
      
      logger.debug('Tenant context extracted successfully', { 
        tenantId: context.tenantId,
        organizationId: context.organizationId,
        isActive: context.isActive
      });
      
      return context;
    } catch (error) {
      logger.error('Error extracting tenant context:', error);
      return null;
    }
  }

  /**
   * Create tenant context from organization data
   */
  createTenantContextFromOrganization(organization: Organization): TenantContext {
    try {
      const context: TenantContext = {
        tenantId: organization.tenantId,
        organizationId: organization.id,
        organizationName: organization.name,
        isActive: organization.status === 'Active',
        features: {
          multiFactorAuth: true,
          federatedIdentity: false,
          customClaims: true,
          auditLogging: true,
          advancedSecurity: true,
          customBranding: true,
          apiAccess: true,
          webhooks: false
        },
        metadata: {
          planType: organization.platformPlanId,
          region: 'default',
          timezone: 'UTC',
          currency: organization.currencyCode || 'USD',
          language: 'en',
          createdAt: organization.dateCreated,
          updatedAt: organization.dateUpdated
        }
      };
      
      logger.debug('Tenant context created from organization', { 
        tenantId: context.tenantId,
        organizationId: context.organizationId,
        isActive: context.isActive
      });
      
      return context;
    } catch (error) {
      logger.error('Error creating tenant context from organization:', error);
      throw error;
    }
  }

  /**
   * Validate tenant access for user with Super Admin bypass
   */
  validateTenantAccess(user: User, requestedTenantId: string): TenantValidationResult {
    try {
      logger.debug('validateTenantAccess called', { 
        userUid: user.uid,
        requestedTenantId,
        userRoles: user.roles
      });

      // Super Admin bypass - always grant access
      if (user.roles?.includes('Super Admin')) {
        logger.debug('Super Admin access granted', { userUid: user.uid, requestedTenantId });
        return {
          isValid: true,
          tenantId: requestedTenantId,
          organizationId: user.organizationId,
          permissions: ['read', 'write', 'admin', 'cross-tenant']
        };
      }

      // Validate user tenant access
      if (user.tenantId !== requestedTenantId) {
        logger.warn('Tenant access denied - user not in requested tenant', {
          userUid: user.uid,
          userTenantId: user.tenantId,
          requestedTenantId
        });
        return {
          isValid: false,
          error: 'User does not have access to the requested tenant',
          permissions: []
        };
      }

      // Validate organization mapping
      if (user.organizationId !== this.getOrganizationIdFromTenantId(requestedTenantId)) {
        logger.warn('Tenant access denied - organization mismatch', {
          userUid: user.uid,
          userOrganizationId: user.organizationId,
          requestedTenantId
        });
        return {
          isValid: false,
          error: 'User organization does not match tenant organization',
          permissions: []
        };
      }

      const result = {
        isValid: true,
        tenantId: requestedTenantId,
        organizationId: user.organizationId,
        permissions: this.getUserPermissions(user)
      };
      
      logger.debug('Tenant access validated successfully', {
        userUid: user.uid,
        tenantId: result.tenantId,
        permissions: result.permissions
      });
      
      return result;
    } catch (error) {
      logger.error('Error validating tenant access:', error);
      return {
        isValid: false,
        error: 'Failed to validate tenant access',
        permissions: []
      };
    }
  }

  /**
   * Create request context for tenant operations
   */
  createRequestContext(user: User, requestId: string): TenantRequestContext {
    try {
      const tenantContext = this.extractTenantContext(user);
      if (!tenantContext) {
        throw new Error('Cannot create request context without valid tenant context');
      }

      const requestContext = {
        requestId,
        tenantId: tenantContext.tenantId,
        organizationId: tenantContext.organizationId,
        userId: user.uid,
        userRoles: user.roles || [],
        timestamp: new Date().toISOString(),
        clubAccess: [] // Added for type compatibility
      };

      logger.debug('Request context created', {
        requestId,
        tenantId: requestContext.tenantId,
        userId: requestContext.userId
      });

      return requestContext;
    } catch (error) {
      logger.error('Error creating request context:', error);
      throw error;
    }
  }

  /**
   * Apply tenant filtering to data arrays
   */
  applyTenantFilter<T extends TenantAwareData>(
    data: T[], 
    tenantContext: TenantContext,
    userContext?: MultiTenantUser
  ): T[] {
    try {
      // Super Admin bypass - can see all data
      if (userContext?.roles?.includes('Super Admin')) {
        logger.debug('Super Admin bypass - returning all data');
        return data;
      }

      if (!this.tenantConfig.enforceStrictAccess) {
        return data;
      }

      const filteredData = data.filter(item => {
        // Check tenant ID match
        if (item.tenantId && item.tenantId !== tenantContext.tenantId) {
          return false;
        }

        // Check organization ID match
        if (item.organizationId && item.organizationId !== tenantContext.organizationId) {
          return false;
        }

        return true;
      });

      logger.debug('Tenant filter applied', {
        originalCount: data.length,
        filteredCount: filteredData.length,
        tenantId: tenantContext.tenantId
      });

      return filteredData;
    } catch (error) {
      logger.error('Error applying tenant filter:', error);
      return [];
    }
  }

  /**
   * Validate data creation for tenant compliance
   */
  validateDataCreation<T extends TenantAwareData>(
    data: Partial<T>,
    tenantContext: TenantContext
  ): { isValid: boolean; error?: string; data?: T } {
    try {
      // Ensure tenant ID is set
      if (!data.tenantId) {
        data.tenantId = tenantContext.tenantId;
      }

      // Ensure organization ID is set
      if (!data.organizationId) {
        data.organizationId = tenantContext.organizationId;
      }

      // Validate tenant ID matches context
      if (data.tenantId !== tenantContext.tenantId) {
        return {
          isValid: false,
          error: 'Data tenant ID does not match user tenant context'
        };
      }

      // Validate organization ID matches context
      if (data.organizationId !== tenantContext.organizationId) {
        return {
          isValid: false,
          error: 'Data organization ID does not match user organization context'
        };
      }

      return {
        isValid: true,
        data: data as T
      };
    } catch (error) {
      logger.error('Error validating data creation:', error);
      return {
        isValid: false,
        error: 'Failed to validate data for tenant compliance'
      };
    }
  }

  /**
   * Create tenant-aware query parameters
   */
  createTenantQuery(tenantContext: TenantContext, clubId?: string): Record<string, string> {
    const query = {
      tenantId: tenantContext.tenantId,
      organizationId: tenantContext.organizationId
    };

    if (clubId) {
      (query as Record<string, string>).clubId = clubId;
    }

    return query;
  }

  /**
   * Audit data access operations
   */
  auditDataAccess(
    requestContext: TenantRequestContext,
    operation: 'read' | 'write' | 'delete',
    resourceType: string,
    resourceId?: string
  ): void {
    if (!this.tenantConfig.auditDataAccess) {
      return;
    }

    try {
      const auditEntry = {
        requestId: requestContext.requestId,
        tenantId: requestContext.tenantId,
        organizationId: requestContext.organizationId,
        userId: requestContext.userId,
        operation,
        resourceType,
        resourceId,
        timestamp: new Date(),
        userRoles: requestContext.userRoles
      };

      logger.info('Data access audit', auditEntry);
    } catch (error) {
      logger.error('Error auditing data access:', error);
    }
  }

  /**
   * Configure tenant settings
   */
  configureTenant(config: Partial<TenantConfig>): void {
    this.tenantConfig = { ...this.tenantConfig, ...config };
    logger.debug('Tenant configuration updated', this.tenantConfig);
  }

  /**
   * Get current tenant configuration
   */
  getTenantConfig(): TenantConfig {
    return { ...this.tenantConfig };
  }

  /**
   * Prepare Firebase tenant configuration for migration
   */
  prepareFirebaseMigration(organization: Organization) {
    return {
      tenantId: organization.tenantId,
      displayName: organization.name,
      allowPasswordSignup: true,
      enableEmailLinkSignin: true,
      multiFactorConfig: {
        state: 'ENABLED' as const
      }
    };
  }

  /**
   * Get tenant settings for organization
   */
  async getTenantSettings(): Promise<{
    features: Record<string, boolean>;
    branding: {
      primaryColor: string;
      logo?: string;
      customDomain?: string;
    };
  }> {
    try {
      return {
        features: {
          multiFactorAuth: true,
          federatedIdentity: false,
          customClaims: true,
          auditLogging: true,
          advancedSecurity: true,
          customBranding: true,
          apiAccess: true,
          webhooks: false
        },
        branding: {
          primaryColor: '#007bff',
          logo: undefined,
          customDomain: undefined
        }
      };
    } catch (error) {
      logger.error('Error getting tenant settings:', error);
      throw error;
    }
  }

  /**
   * Get user permissions based on roles
   */
  private getUserPermissions(user: User): string[] {
    const roles = user.roles || [];
    const permissions: string[] = [];

    if (roles.includes('Super Admin')) {
      permissions.push('read', 'write', 'admin', 'cross-tenant');
    } else if (roles.includes('Admin')) {
      permissions.push('read', 'write', 'admin');
    } else if (roles.includes('Staff') || roles.includes('Member')) {
      permissions.push('read', 'write');
    } else {
      permissions.push('read');
    }

    return permissions;
  }

  /**
   * Map tenant ID to organization ID
   */
  private getOrganizationIdFromTenantId(tenantId: string): string {
    // In simplified implementation, tenant ID maps directly to organization ID
    // This can be enhanced with actual mapping logic if needed
    return tenantId;
  }
}

// Export getter function for lazy initialization
export const getTenantManager = (): TenantManager => TenantManager.getInstance();

// Also provide a cached singleton reference for cases where direct access is needed
// This won't cause multiple instantiations since getInstance() already handles the singleton pattern
let _cachedInstance: TenantManager | null = null;
export const tenantManager = new Proxy({} as TenantManager, {
  get(target, prop) {
    if (!_cachedInstance) {
      _cachedInstance = TenantManager.getInstance();
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (_cachedInstance as any)[prop];
  }
}); 
